/** @format */
import { Document } from '@contentful/rich-text-types';
import { LinkTarget } from '@mpb-com/feather/components/atoms/link';
import { ReactNode } from 'react';

import { ArticleContentModel } from '@toucan/types/contentful/article';

import { ContentfulQueryId } from '@toucan/common/contentful/config/enums';
import {
    SectionPreviewBlockImagePosition,
    SectionPreviewBlockVariant,
} from '@toucan/features/contentful/components/section-preview-block';

import { ContentCollectionVariant, SiteSection } from './enums';

export type Renderers = Record<string, (args: any) => JSX.Element | null>;

export type ContentfulQueryDefinitions = Record<ContentfulQueryId, string>;

export type Fragment = (string | Fragment)[];

export type Image = {
    __typename: string;
    url: string | null;
    urlFallback: string | null;
    contentType: string | null;
    contentTypeFallback: string | null;
    title: string | null;
    description: string | null;
    width?: number;
    height?: number;
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface GetHomepageDataResponse {
    contentBlockCollectionCollection: {
        items: ContentBlockCollection[];
    };
    originalContentGroupCollection: {
        items: OriginalContentGroup[];
    };
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface getCategoryCardsResponse {
    results: ContentHubCategoryData[];
    total: number;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentBlockCollection extends Block {
    contentCollection: {
        items: ContentfulBasicContentBlock[];
    } | null;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface Block {
    __typename: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SiteHeaderFields extends Block {
    title: string;
    subtitle: RichText | null;
    description: RichText | null;
    image: Image | null;
    variant: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SiteModalBlockFields extends Block {
    content: RichText;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentAreaFields extends Block {
    contentCollection: {
        items: ContentfulBasicContentBlock[];
    } | null;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentHubCategoryData extends Omit<ContentCardData, 'title'> {
    name: string;
    originallyPublishedAt: string | null;
} // Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface Tag {
    __typename: string;
    sys: {
        id: string;
    };
    name: string;
    urlSlug: string;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CategoryPageMetaQueryData {
    contentCategoryCollection: {
        items: CategoryPageData[];
    };
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CategoryPageContentQueryData {
    contentCategoryCollection: {
        items: { content: RichText }[];
    };
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CategoryPageData extends UrlSlugParamsType {
    __typename: string;
    sys: {
        id: string;
        publishedAt: string;
    };
    name: string;
    urlSlug: string;
    teaser: string;
    description: string | null;
    thumbnail: Image;
    heroImage: Image | null;
    seoTitle: string | null;
    seoDescription: string | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ArticleCollection {
    items: ArticleCardData[];
    total: number;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentCardData {
    __typename: ContentfulEntryType;
    sys: {
        id: string;
        publishedAt: string;
    };
    title: string;
    thumbnail: Image | null;
    thumbnailFallback: Image;
    urlSlug: string | null;
    teaser: string | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface StoryCardData extends ContentCardData {
    originallyPublishedAt?: string;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentfulBasicContentBlock {
    __typename: string | null;
    title: string;
    image: Image;
    link: string | null;
    linkTitle: string | null;
    description: RichText | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface BasicContentBlock {
    __typename: string | null;
    title: string;
    image: Image;
    link: string | null;
    linkTitle: string | null;
    description: ReactNode | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface OriginalContentGroup {
    contentCollection: {
        items: ArticleCardData[];
    };
}

export enum ContentfulEntryType {
    BASIC_PAGE = 'PageHelp',
    CATEGORY = 'ContentCategory',
    ARTICLE = 'Article',
    LANDING_PAGE = 'LandingPage',
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TwoColumnWrapper {
    __typename: string;
    sys: {
        id: string;
    };
    title: string;
    columnOneContent: RichText;
    columnTwoContent: RichText;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SiteContentContentCollection {
    __typename: string;
    sys: {
        id: string;
    };
    title: string;
    renderType: ContentCollectionVariant;
    heading: string | null;
    contentBlocksCollection: {
        items: ContentfulBasicContentBlock[];
    } | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface OriginalContentNavItem {
    urlSlug: string;
    teaser: string;
    name: string;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SiteContentEmbed {
    __typename: string;
    sys: {
        id: string;
    };
    embedTarget: string;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ArticleCardData extends ContentCardData {
    originallyPublishedAt: string | null;
    category: {
        urlSlug: string;
    };
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SitewideBannerData {
    message: RichText;
    theme: string;
    sys: {
        id: string;
    };
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentfulCallToAction {
    __typename: string;
    sys: {
        id: string;
    };
    buttonLabel: string;
    buttonUrl: string;
    description: RichText | null;
    title: string | null;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SiteContentEnhancedLink {
    __typename: string;
    sys: {
        id: string;
    };
    url: string;
    content: string;
    target: LinkTarget;
}

export type BaseQueryVariables = {
    locale: string;
    queryId?: ContentfulQueryId;
};

export type QueryVariables = BaseQueryVariables & {
    siteSection: SiteSection | SiteSection[];
};

export type EntitySpecificQueryVariables = {
    entityId: string;
};

export type SiteContentMetadata = {
    title: string;
    entityId: string;
};

export type SiteContentAccordion = {
    title: string;
    content: RichText;
    sys: {
        id: string;
    };
};

export type SiteContentScript = {
    src: string;
    attributes?: Record<string, string>;
    sys: {
        id: string;
    };
};

export type SiteContentSectionPreviewBlock = {
    __typename: string;
    heading: string;
    sectionPreviewDescription: string;
    image: Image;
    callToAction: ContentfulCallToAction;
    imagePosition: SectionPreviewBlockImagePosition;
    variant: SectionPreviewBlockVariant;
    sys: {
        id: string;
    };
};

export type ContentfulTableauDashboardProps = {
    src: string;
}; // Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface RichText {
    json: Document;
    links?: Links;
} // Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface Links {
    assets: {
        block: Image[];
    };
    entries: {
        hyperlink: HyperlinkEntry[];
        block: BlockEntry[];
        inline: BlockEntry[];
    };
} // Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface BlockEntry {
    __typename: string;
    sys: {
        id: string;
    };
    [key: string]: any;
}

export type HyperlinkEntry = {
    __typename: ContentfulEntryType;
    sys: {
        id: string;
    };
    title: string;
    urlSlug: string;
};

export type ArticleHyperlinkEntry = HyperlinkEntry & {
    __typename: ContentfulEntryType.ARTICLE;
    category: ArticleContentModel['category'];
}; // Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CmsBasicPageData extends UrlSlugParamsType {
    __typename: string;
    sys: {
        id: string;
    };
    title: string;
    description: RichText | null;
    content: RichText;
    seoTitle: string | null;
    seoDescription: string | null;
}
export type UrlSlugParamsType = {
    urlSlug_GB: string | null;
    urlSlug_EU: string | null;
    urlSlug_US: string | null;
    urlSlug_DE: string | null;
    urlSlug_FR: string | null;
    urlSlug_NL: string | null;
    urlSlug_IT: string | null;
    urlSlug_ES: string | null;
    urlSlug_PT: string | null;
    urlSlug_EN_IE: string | null;
    urlSlug_DE_AT: string | null;
    urlSlug_FR_BE: string | null;
    urlSlug_NL_BE: string | null;
};
export type PageDataType =
    | SiteHeaderFields
    | ContentAreaFields
    | ContentBlockCollection;

export type PageData = Record<string, PageDataType[]>;
